import { useEffect, useState } from "react";
import { ProductsTypes } from "../../../../api/types/products.types";
import ProductsService from "../../../../api/services/products.service";
import ProductCard from "../../../common/product-card/ProductCard";
import styles from "./RelatedProducts.module.scss";
import Loading from "../../../common/loading/Loading";
import { useTranslation } from "react-i18next";
import { useCurrencies } from "../../../../context/CurrenciesContext";
import { useLanguage } from "../../../../context/LanguageContext";
import { groupProductVariants } from "../../../../utils/groupProductVariants";

interface Props {
  categoryId?: number;
  currentProductId: number;
}

const RelatedProducts = ({ categoryId, currentProductId }: Props) => {
  const [products, setProducts] = useState<ProductsTypes[]>([]);
  const [loading, setLoading] = useState(false);
  const { currencyId } = useCurrencies();
  const { t } = useTranslation("translation");
  const { currentLanguage } = useLanguage();

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!currencyId || !categoryId) {
        return;
      }

      setLoading(true);
      try {
        const res = await ProductsService.filterProducts(
          categoryId,
          Number(currencyId),
        );

        let variants = res?.data?.data || [];

        const filteredVariants = variants.filter(
          (v: ProductsTypes) => v.product_id !== currentProductId,
        );

        const groupedProducts = groupProductVariants(filteredVariants);

        setProducts(groupedProducts.slice(0, 8));
      } catch (error) {
        console.error("Related products error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [categoryId, currentProductId, currencyId]);

  const getLocalizedName = (product: ProductsTypes) => {
    switch (currentLanguage) {
      case "az":
        return product.name_az || product.name_en;
      case "de":
        return product.name_de || product.name_en;
      case "es":
        return product.name_es || product.name_en;
      case "en":
      default:
        return product.name_en;
    }
  };

  if (loading) {
    return (
      <section className={styles.related_products}>
        <div className="container">
          <div className={styles.loading_container}>
            <Loading />
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return null;
  }

  return (
    <section className={styles.related_products}>
      <div className="container">
        <h2 className={styles.section_title}>{t("related_products")}</h2>
        <div className={styles.products_list}>
          {products.map((p) => (
            <ProductCard
              id={p.id}
              key={p.id}
              title={getLocalizedName(p)}
              currency={p.currency_name}
              price={p.price?.toString()}
              discount_percent={p.discount_percent?.toString()}
              discount_price={p.discount_price?.toString()}
              image={p.images ? p.images[0]?.image : ""}
              stock_quantity={p.stock_quantity}
              wishlist={p.is_wish}
              images={p.images}
              attributes={p.attributes}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedProducts;
